import React, { useState, useEffect, useMemo } from 'react';
import '../../components/UserDetail/UserDetail.css';
import VerifyAccountModal from '../VerifyAccountModal/VerifyAccountModal';
import { BiLogOutCircle } from "react-icons/bi";
import { MdAssuredWorkload } from "react-icons/md";
import { getAuthToken, removeAuthToken, getUserData } from "../../utils/auth";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdArrowDropDown } from "react-icons/md";
import { VscGraphLine } from "react-icons/vsc";
import { HiOutlineCube } from "react-icons/hi";
import Analytics from '../DashboardModule/Analytics';
import MyOrder from '../DashboardModule/MyOrder';
import RFQ from '../DashboardModule/RFQ';
import QuotationReceived from '../DashboardModule/QuotationReceived';
import RA from '../DashboardModule/RA';
import CancelRequest from '../DashboardModule/CancelRequest';
import ExpiredRequest from '../DashboardModule/ExpiredRequest';
import { getUserDetails } from "../../API/Api";
import NotVerifiedModal from '../DashboardModule/NotVerifiedModal';
import logostelmart from '../../assets/images/logomain.png';
import LoanAssistance from '../DashboardModule/LoanAssistance';

const UserDetail = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [showNotVerifiedModal, setShowNotVerifiedModal] = useState(false);
	const [activeQuotationTab, setActiveQuotationTab] = useState(null);
	const [isQuotationOpen, setIsQuotationOpen] = useState(false);
	const [isQuotationMainSelected, setIsQuotationMainSelected] = useState(false);
	const [activeTab, setActiveTab] = useState('business');
	const [activeLoanTab, setActiveLoanTab] = useState(false);
	const [user, setUser] = useState(null);
	const navigate = useNavigate();
	const storedUserData = useMemo(() => getUserData(), []);

	useEffect(() => {
		const token = getAuthToken();
		if (!token) {
			toast.error("Please login to access dashboard");
			navigate("/");
			return;
		}

		const fetchUser = async () => {
			try {
				const userData = await getUserDetails();
				setUser(userData.user);

				// Show verification modal if user is not verified
				if (userData.user?.verified !== 1) {
					// setShowNotVerifiedModal(true);
					const interval = setInterval(() => {
						setShowNotVerifiedModal(true);
					}, 30000); // 30 seconds

					return () => clearInterval(interval);
				}
			} catch (error) {
				console.error("Error fetching user details:", error);
				toast.error("Failed to load user data");
				if (error.response?.status === 401) {
					removeAuthToken();
					navigate("/");
				}
			}
		};

		fetchUser();
	}, [navigate, storedUserData]);

	// useEffect(() => {
	//     const handleClickOutside = (e) => {
	//         if (isQuotationOpen &&
	//             !e.target.closest('.quotation-parent') &&
	//             !e.target.closest('.dropdown-container')) {
	//             setIsQuotationOpen(false);
	//         }
	//     };

	//     document.addEventListener('click', handleClickOutside);
	//     return () => document.removeEventListener('click', handleClickOutside);
	// }, [isQuotationOpen]);

	const handleQuotationTabClick = (tab) => {
		setActiveTab("settings");
		setActiveQuotationTab(tab);
		setIsQuotationMainSelected(false);
	};

	const handleQuotationMainClick = () => {
		setActiveTab("settings");
		setActiveQuotationTab(null);
		setIsQuotationMainSelected(true);
		setIsQuotationOpen(!isQuotationOpen);
	};

	const openModal = () => setIsModalOpen(true);
	const closeModal = () => setIsModalOpen(false);

	const handleTabClick = (tab) => {
		setActiveTab(tab);
		if (tab !== "settings") {
			setIsQuotationOpen(false);
		}
		if (tab === "settings") {
			setActiveQuotationTab(null);
			setIsQuotationMainSelected(false);
		}
	};

	const handleLogout = () => {
		removeAuthToken();
		toast.success("You have been logged out successfully");
		setTimeout(() => {
			window.location.href = "/"
		}, 1000);
	};

	if (!storedUserData) {
		return (
			<div className="access-denied2">
				<h2>Loading user data...</h2>
			</div>
		);
	}

	if (storedUserData?.userType !== 'buyer') {
		return (
			<div className="access-denied2">
				<h2>Access Denied</h2>
				<p>This dashboard is only available for buyers.</p>
			</div>
		);
	}

	const truncateText = (text, wordLimit = 5) => {
		if (!text) return '';
		const words = text.split(' ');
		return words.length > wordLimit
			? words.slice(0, wordLimit).join(' ') + '...'
			: text;
	};

	const getUserInitials = (firstName, lastName) => {
		let initials = '';
		if (firstName && firstName.trim()) initials += firstName.trim()[0].toUpperCase();
		if (lastName && lastName.trim()) initials += lastName.trim()[0].toUpperCase();
		return initials || 'U'; // Fallback to 'U' if no name is available
	};


	return (
		<div className="verification-container">
			<ToastContainer position="top-right" autoClose={5000} />

			{/* Not Verified Modal */}
			{showNotVerifiedModal && user?.verified !== 1 && (
				<NotVerifiedModal
					onClose={() => setShowNotVerifiedModal(false)}
				/>
			)}

			<VerifyAccountModal
				isOpen={isModalOpen}
				onClose={closeModal}
				user={user}
			/>

			<aside className="sidebar-three">
				<div className="user-info">
					{user?.image ? (
						<img src={user.image} alt="User" className="user-avatar" />
					) : (
						<div className="user-initials">
							{getUserInitials(user?.first_name, user?.last_name)}
						</div>
					)}
					<div className="user-details">
						<h3 title={user?.first_name}>{truncateText(user?.first_name) || 'User'}</h3>
						<p title={user?.ph_number}>{truncateText(user?.ph_number)}</p>
					</div>
				</div>
				{user?.stelmart_status === 'Approved' ? (
					<button className="verify-btn-success">
						<img src={logostelmart} alt="Logo Stelmart" width="30" height="30" />
						Verified Account
					</button>
				) : user?.stelmart_status === 'Pending' && user?.verified == 1 ? (
					<button className="verify-btn">
						Pending Account
					</button>
				) : (
					<button className="verify-btn" onClick={openModal}>
						Verify Account
					</button>
				)}


				<nav className="nav-links">
					<ul>
						<li
							onClick={() => handleTabClick("business")}
							className={activeTab === "business" ? "active" : ""}
						>
							<VscGraphLine className="icon" /> Analytics
						</li>
						<li
							onClick={() => handleTabClick("address")}
							className={activeTab === "address" ? "active" : ""}
						>
							<HiOutlineCube className="icon" /> My Orders
						</li>

						<li className={`quotation-parent ${activeTab === "settings" ? "active" : ""}`}>
							<div className="quotation-header" onClick={(e) => {
								e.stopPropagation();
								setIsQuotationOpen((prev) => !prev);
							}}>
								<div
									className="quotation-title"
									style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
								>
									<span style={{ display: "flex", alignItems: "center" }}>
										<span className="icon">
											<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
												<path d="M1.92.506a.5.5 0 0 1 .434.14L3 1.293l.646-.647a.5.5 0 0 1 .708 0L5 1.293l.646-.647a.5.5 0 0 1 .708 0L7 1.293l.646-.647a.5.5 0 0 1 .708 0L9 1.293l.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .801.13l.5 1A.5.5 0 0 1 15 2v12a.5.5 0 0 1-.053.224l-.5 1a.5.5 0 0 1-.8.13L13 14.707l-.646.647a.5.5 0 0 1-.708 0L11 14.707l-.646.647a.5.5 0 0 1-.708 0L9 14.707l-.646.647a.5.5 0 0 1-.708 0L7 14.707l-.646.647a.5.5 0 0 1-.708 0L5 14.707l-.646.647a.5.5 0 0 1-.708 0L3 14.707l-.646.647a.5.5 0 0 1-.801-.13l-.5-1A.5.5 0 0 1 1 14V2a.5.5 0 0 1 .053-.224l.5-1a.5.5 0 0 1 .367-.27m.217 1.338L2 2.118v11.764l.137.274.51-.51a.5.5 0 0 1 .707 0l.646.647.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.646.646.646-.646a.5.5 0 0 1 .708 0l.509.509.137-.274V2.118l-.137-.274-.51.51a.5.5 0 0 1-.707 0L12 1.707l-.646.647a.5.5 0 0 1-.708 0L10 1.707l-.646.647a.5.5 0 0 1-.708 0L8 1.707l-.646.647a.5.5 0 0 1-.708 0L6 1.707l-.646.647a.5.5 0 0 1-.708 0L4 1.707l-.646.647a.5.5 0 0 1-.708 0z" />
												<path d="M3 4.5a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 1 1 0 1h-6a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5m8-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5" />
											</svg>
										</span>
										<span> My Quotations</span>
									</span>
									<MdArrowDropDown
										className={`dropdown-icon ${isQuotationOpen ? "open" : ""}`}
										onClick={(e) => {
											e.stopPropagation();
											setIsQuotationOpen(!isQuotationOpen);
										}}
									/>
								</div>

							</div>

							{isQuotationOpen && (
								<div
									className={`dropdown-container ${isQuotationOpen ? 'open' : ''}`}
									onClick={(e) => e.stopPropagation()}
								>
									<ul>
										<li
											className={`dropdown-item ${activeQuotationTab === "rfq-sent" ? "active" : ""}`}
											onClick={(e) => {
												e.stopPropagation();
												handleQuotationTabClick("rfq-sent");
											}}
										>
											<span className="indent">RFQ Sent</span>
										</li>
										<li
											className={`dropdown-item ${activeQuotationTab === "quotation-received" ? "active" : ""}`}
											onClick={(e) => {
												e.stopPropagation();
												handleQuotationTabClick("quotation-received");
											}}
										>
											<span className="indent">Quotation Received</span>
										</li>
										<li
											className={`dropdown-item ${activeQuotationTab === "ra" ? "active" : ""}`}
											onClick={(e) => {
												e.stopPropagation();
												handleQuotationTabClick("ra");
											}}
										>
											<span className="indent">Reverse Auction(RA)</span>
										</li>
										<li
											className={`dropdown-item ${activeQuotationTab === "cancelled-requests" ? "active" : ""}`}
											onClick={(e) => {
												e.stopPropagation();
												handleQuotationTabClick("cancelled-requests");
											}}
										>
											<span className="indent">Cancelled Requests</span>
										</li>
										<li
											className={`dropdown-item ${activeQuotationTab === "expired-request" ? "active" : ""}`}
											onClick={(e) => {
												e.stopPropagation();
												handleQuotationTabClick("expired-request");
											}}
										>
											<span className="indent">Expired Request</span>
										</li>
									</ul>
								</div>
							)}
						</li>
						<li
							className={`dropdown-item ${activeQuotationTab === "loan-assistance" ? "active" : ""}`}
							onClick={(e) => {
								e.stopPropagation();
								handleQuotationTabClick("loan-assistance");
							}}
						>
							<span className="indent">    <MdAssuredWorkload className="icon" />
								Loan Assistance</span>
						</li>



						<li className="logout" onClick={handleLogout}>
							<BiLogOutCircle className="icon" />
							Logout
						</li>

					</ul>
				</nav>
			</aside>

			<main className="main-content-analy">
				<div className="content-area">
					{activeTab === "business" && <Analytics />}
					{activeTab === "address" && <MyOrder />}
					{activeTab === "settings" && (
						<>
							{activeQuotationTab === "rfq-sent" ? (
								<RFQ />
							) : activeQuotationTab === "quotation-received" ? (
								<QuotationReceived />
							) : activeQuotationTab === "ra" ? (
								<RA />
							) : activeQuotationTab === "cancelled-requests" ? (
								<CancelRequest />
							) : activeQuotationTab === "expired-request" ? (
								<ExpiredRequest />
							) : activeQuotationTab === "loan-assistance" ? (
								<LoanAssistance />
							) : (
								// <MyQuotations activeSubTab={activeQuotationTab} />
								null
							)}
						</>
					)}

				</div>
			</main>

		</div>
	);
};

export default UserDetail;